import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { alpha } from "@mui/material/styles";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { formatDiaMes, simboloById, todayIso } from "./designCalendarModel";
import { DesignCalendarSymbolStack } from "./DesignCalendarSymbolIcon";

const ACCENT = "#ff5a00";
const BANNER_POS_KEY = "crm.designCalendarBanner.position";
const BANNER_OPEN_KEY = "crm.designCalendarBanner.open";

function readBannerPosition() {
  try {
    const raw = localStorage.getItem(BANNER_POS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Number.isFinite(parsed?.x) || !Number.isFinite(parsed?.y)) return null;
    return { x: parsed.x, y: parsed.y };
  } catch {
    return null;
  }
}

function readBannerOpen() {
  try {
    return localStorage.getItem(BANNER_OPEN_KEY) === "1";
  } catch {
    return false;
  }
}

function clampBannerPosition(x, y, width, height) {
  const maxX = Math.max(8, window.innerWidth - width - 8);
  const maxY = Math.max(8, window.innerHeight - height - 8);
  return {
    x: Math.min(Math.max(8, x), maxX),
    y: Math.min(Math.max(8, y), maxY),
  };
}

export default function DesignCalendarBanner({ items = [], onMarcarVisto }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const boxRef = useRef(null);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const [pos, setPos] = useState(readBannerPosition);
  const [dragging, setDragging] = useState(false);
  const [open, setOpen] = useState(readBannerOpen);
  const [pendingKey, setPendingKey] = useState(null);

  useEffect(() => {
    if (!pos || !boxRef.current) return undefined;
    const fit = () => {
      const rect = boxRef.current?.getBoundingClientRect();
      if (!rect) return;
      const next = clampBannerPosition(pos.x, pos.y, rect.width, rect.height);
      if (next.x === pos.x && next.y === pos.y) return;
      setPos(next);
      localStorage.setItem(BANNER_POS_KEY, JSON.stringify(next));
    };
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [pos, open]);

  if (!items.length || typeof document === "undefined") return null;

  const month = Number(todayIso().slice(5, 7));
  const openCalendar = () => navigate(`/design-calendar#mes-${month}`);

  /** Abre el calendario y posiciona el arranque concreto en Por hacer. */
  const openAccion = (item) => {
    const fecha = String(item?.fecha || "").slice(0, 10);
    const year = Number(fecha.slice(0, 4));
    const mes = Number(fecha.slice(5, 7));
    const id = Number(item?.id);
    if (!Number.isInteger(id) || id <= 0) {
      openCalendar();
      return;
    }
    navigate(`/design-calendar#accion-${id}`, {
      state: {
        designCalendarYear: Number.isInteger(year) ? year : undefined,
        designCalendarMonth: Number.isInteger(mes) ? mes : undefined,
      },
    });
  };

  const toggleOpen = () => {
    setOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(BANNER_OPEN_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const onPointerDown = (event) => {
    if (event.button != null && event.button !== 0) return;
    if (
      event.target?.closest?.(
        "button, a, input, textarea, select, [data-design-calendar-no-drag]",
      )
    ) {
      return;
    }
    const rect = boxRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: rect.left,
      originY: rect.top,
      width: rect.width,
      height: rect.height,
      moved: false,
      last: { x: rect.left, y: rect.top },
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (!drag.moved && Math.hypot(dx, dy) < 5) return;
    drag.moved = true;
    const next = clampBannerPosition(
      drag.originX + dx,
      drag.originY + dy,
      drag.width,
      drag.height,
    );
    drag.last = next;
    setDragging(true);
    setPos(next);
  };

  const onPointerUp = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    setDragging(false);
    if (!drag.moved) return;
    suppressClickRef.current = true;
    localStorage.setItem(BANNER_POS_KEY, JSON.stringify(drag.last));
  };

  const marcarVisto = async (item) => {
    const key = `${item.kind}-${item.id}`;
    if (pendingKey || !onMarcarVisto) return;
    setPendingKey(key);
    try {
      await onMarcarVisto(item.kind, item.id);
    } catch (error) {
      console.error(error);
      window.alert(
        t("designCalendar.vistoFailed", "No se ha podido marcar como visto"),
      );
    } finally {
      setPendingKey(null);
    }
  };

  return createPortal(
    <Box
      ref={boxRef}
      role="status"
      aria-live="polite"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onClickCapture={(event) => {
        if (!suppressClickRef.current) return;
        suppressClickRef.current = false;
        event.preventDefault();
        event.stopPropagation();
      }}
      sx={(theme) => ({
        position: "fixed",
        top: pos ? pos.y : "auto",
        left: pos ? pos.x : "auto",
        right: pos ? "auto" : 16,
        bottom: pos ? "auto" : 16,
        zIndex: theme.zIndex.modal - 1,
        width: open ? "min(380px, calc(100vw - 24px))" : "auto",
        maxWidth: "min(380px, calc(100vw - 24px))",
        touchAction: "none",
        cursor: dragging ? "grabbing" : "grab",
        userSelect: "none",
        "& button, & [data-design-calendar-no-drag]": {
          cursor: "pointer",
          touchAction: "manipulation",
          userSelect: "auto",
        },
        "& [data-design-calendar-no-drag]": {
          cursor: "default",
        },
      })}
    >
      <Box
        sx={(theme) => ({
          borderRadius: "16px",
          px: 0.85,
          pt: 0.45,
          pb: open ? 0.75 : 0.45,
          bgcolor: alpha(ACCENT, theme.palette.mode === "dark" ? 0.22 : 0.14),
          border: "2px solid",
          borderColor: ACCENT,
          boxShadow: `0 12px 32px ${alpha(ACCENT, 0.35)}`,
          backdropFilter: "blur(16px)",
        })}
      >
        <Stack
          direction="row"
          spacing={0.6}
          alignItems="center"
          onClick={(event) => {
            if (event.target?.closest?.("button")) return;
            toggleOpen();
          }}
          sx={{
            px: 0.35,
            py: 0.15,
            cursor: dragging ? "grabbing" : "grab",
            borderRadius: 1.5,
            "&:hover": { bgcolor: alpha(ACCENT, 0.06) },
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: ACCENT,
              boxShadow: `0 0 0 4px ${alpha(ACCENT, 0.16)}`,
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontWeight: 750,
              fontSize: "0.75rem",
              flex: 1,
              whiteSpace: "nowrap",
            }}
          >
            {t("designCalendar.bannerTitle", "Por hacer")}
          </Typography>
          <Box
            sx={{
              minWidth: 22,
              height: 22,
              px: 0.65,
              borderRadius: "50%",
              bgcolor: ACCENT,
              color: "#000",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.78rem",
              fontWeight: 900,
              border: "1.5px solid #000",
            }}
          >
            {items.length}
          </Box>
          <Button
            size="small"
            onClick={openCalendar}
            startIcon={<CalendarMonthOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{
              minWidth: 0,
              px: 1,
              py: 0.2,
              fontSize: "0.72rem",
              fontWeight: 750,
              textTransform: "none",
              color: ACCENT,
            }}
          >
            {t("designCalendar.open", "Abrir")}
          </Button>
          <IconButton
            size="small"
            aria-label={
              open
                ? t("designCalendar.collapse", "Cerrar")
                : t("designCalendar.expand", "Ver avisos")
            }
            onClick={(event) => {
              event.stopPropagation();
              toggleOpen();
            }}
            sx={{ p: 0.25 }}
          >
            {open ? (
              <ExpandLessRoundedIcon sx={{ fontSize: 18 }} />
            ) : (
              <ExpandMoreRoundedIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Stack>
        <Collapse in={open} unmountOnExit>
          <Stack
            data-design-calendar-no-drag="1"
            onPointerDown={(event) => event.stopPropagation()}
            spacing={0.45}
            sx={{
              mt: 0.6,
              maxHeight: "min(42vh, 320px)",
              overflow: "auto",
              pr: 0.15,
              cursor: "default",
              touchAction: "manipulation",
            }}
          >
            {items.map((item) => {
              const ids =
                Array.isArray(item.simbolos) && item.simbolos.length
                  ? item.simbolos
                  : [item.simbolo || "nota"];
              const simbolo = simboloById(ids[0]);
              const busy = pendingKey === `${item.kind}-${item.id}`;
              return (
                <Box
                  key={`${item.kind}-${item.id}`}
                  sx={{
                    px: 0.9,
                    py: 0.65,
                    borderRadius: 1.5,
                    bgcolor: alpha(simbolo.color, 0.08),
                    border: "1px solid",
                    borderColor: alpha(simbolo.color, 0.2),
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      fontWeight: 750,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.6,
                      minWidth: 0,
                    }}
                    noWrap
                  >
                    <DesignCalendarSymbolStack ids={ids} size={20} max={3} />
                    <Box
                      component="span"
                      sx={{
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.texto}
                    </Box>
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.68rem", opacity: 0.72, pl: 1.6 }}
                  >
                    {formatDiaMes(item.fecha)}
                    {" · "}
                    {t("designCalendar.kindStart", "Arranque")}
                  </Typography>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.55 }}>
                    <Button
                      size="small"
                      disabled={busy}
                      onClick={() => openAccion(item)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 750,
                        fontSize: "0.7rem",
                        minHeight: 26,
                        py: 0.15,
                        color: ACCENT,
                      }}
                    >
                      {t("designCalendar.open", "Abrir")}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={busy || !onMarcarVisto}
                      onClick={() => marcarVisto(item)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 800,
                        fontSize: "0.7rem",
                        minHeight: 26,
                        py: 0.15,
                        bgcolor: ACCENT,
                        color: "#000",
                        "&:hover": { bgcolor: "#ff6a1a", color: "#000" },
                      }}
                    >
                      {t("designCalendar.markSeen", "Visto")}
                    </Button>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Collapse>
      </Box>
    </Box>,
    document.body,
  );
}
